// The table's state transitions. A query change returns to page 1 and clears expansion and every selection
// scope. A change to which columns are hidden also returns to page 1 and clears every selection scope, and
// keeps expansion while the rows on screen stay the same. A page or page-size change clears page-scoped
// selection and keeps query-scoped selection. Every change records the reason of the action that made it, for
// publication.

import { ACTIONS } from '../types'
import { enumSelection, enumValue, pruneFilters } from '../query/filters'
import { nextSort, reconcileSort } from '../query/sort'

const NO_SELECTION = () => ({
  scope: 'none',
  ids: new Set(),
  excluded: new Set(),
})

// the refined option of each enumerable filter that declares a refinement, by column key
const refinementsOf = columns => {
  const out = {}
  columns.forEach(column => {
    if (column.refinement && column.refinement.option)
      out[column.key] = column.refinement.option
  })
  return out
}

/**
 * @param {import('../types').NormalizedConfig} config
 * @returns {import('../types').TableState}
 */
export const initialState = config => ({
  searchDraft: '',
  query: { search: '', filters: {}, sort: config.defaultSort },
  page: 1,
  pageSize: config.defaultPageSize,
  expanded: new Set(),
  view: {
    density: config.defaultDensity,
    order: config.columns.map(column => column.key),
    hidden: config.columns
      .filter(column => column.visible === false)
      .map(column => column.key),
    widths: {},
  },
  selection: NO_SELECTION(),
  // what reconciling the query needs from the configuration
  defaults: {
    sort: config.defaultSort,
    refinements: refinementsOf(config.columns),
  },
  reason: null,
})

const pageScoped = selection =>
  selection.scope === 'rows' || selection.scope === 'page'

const withoutPageSelection = state =>
  pageScoped(state.selection) ? NO_SELECTION() : state.selection

const firstPage = state => ({
  ...state,
  page: 1,
  expanded: new Set(),
  selection: NO_SELECTION(),
})

const sameKeys = (a = [], b = []) => {
  const before = new Set(a)
  const after = new Set(b)
  return before.size === after.size && [...before].every(key => after.has(key))
}

// key order does not matter, so a restored query equal to the current one is not a change
const stable = value => {
  if (Array.isArray(value)) return value.map(stable)
  if (!value || typeof value !== 'object') return value
  const out = {}
  Object.keys(value)
    .sort()
    .forEach(key => {
      out[key] = stable(value[key])
    })
  return out
}

const sameQuery = (a, b) =>
  JSON.stringify(stable(a)) === JSON.stringify(stable(b))

const withQuery = (state, query) =>
  firstPage({ ...state, query: { ...state.query, ...query } })

// Columns a configured filter can no longer use; their filters drop and a user sort on them gives way.
const reconcileQuery = (state, defaultSort) => {
  const { order, hidden } = state.view
  const filters = pruneFilters(state.query.filters, { keys: order, hidden })
  const sort = reconcileSort(state.query.sort, defaultSort, {
    keys: order,
    hidden,
  })
  const changed =
    sort !== state.query.sort ||
    Object.keys(filters).length !==
      Object.keys(state.query.filters || {}).length
  return changed ? withQuery(state, { filters, sort }) : state
}

// Keeps a refinement only while its refined option is selected; numeric and date values pass through.
const settle = (state, key, value) => {
  if (!(Array.isArray(value) || (value && Array.isArray(value.selected))))
    return value
  const option = (state.defaults && state.defaults.refinements[key]) || null
  return enumValue(
    { refinement: option ? { option } : null },
    enumSelection(value),
    Boolean(value.refined)
  )
}

// Setting a filter to an inactive value (an empty selection, null, no bounds) clears it.
const setFilter = (state, action) => {
  const filters = {
    ...state.query.filters,
    [action.key]: settle(state, action.key, action.value),
  }
  return withQuery(state, { filters: pruneFilters(filters) })
}

const transition = (state, action) => {
  switch (action.type) {
    case ACTIONS.searchDraft:
      return { ...state, searchDraft: action.value }
    case ACTIONS.searchCommit: {
      // the trimmed value commits; an empty value clears search
      const search = (action.value || '').trim()
      if (search === state.query.search) return state
      return withQuery(state, { search })
    }
    case ACTIONS.filterSet:
      return setFilter(state, action)
    case ACTIONS.queryClear:
      return withQuery(
        { ...state, searchDraft: '' },
        { search: '', filters: {} }
      )
    case ACTIONS.sortToggle:
      return withQuery(state, {
        sort: nextSort(state.query.sort, action.key, action.defaultSort),
      })
    case ACTIONS.pageSet:
    case ACTIONS.pageClamp:
      if (action.page === state.page) return state
      return {
        ...state,
        page: action.page,
        expanded: new Set(),
        selection: withoutPageSelection(state),
      }
    case ACTIONS.pageSizeSet:
      if (action.size === state.pageSize) return state
      return {
        ...state,
        pageSize: action.size,
        page: 1,
        expanded: new Set(),
        selection: withoutPageSelection(state),
      }
    case ACTIONS.rowToggle: {
      const expanded = new Set(state.expanded)
      if (expanded.has(action.id)) expanded.delete(action.id)
      else expanded.add(action.id)
      return { ...state, expanded }
    }
    case ACTIONS.expansionClear:
      if (!state.expanded.size) return state
      return { ...state, expanded: new Set() }
    case ACTIONS.viewSet: {
      const next = { ...state, view: { ...state.view, ...action.view } }
      const defaultSort =
        action.defaultSort || (state.defaults && state.defaults.sort)
      const reconciled = defaultSort ? reconcileQuery(next, defaultSort) : next
      // showing or hiding a column returns to page 1 and clears every selection scope in the same commit
      if (sameKeys(state.view.hidden, next.view.hidden)) return reconciled
      const moved = firstPage(reconciled)
      // expansion survives when the table was already on page 1 and the query did not change; a search
      // over visible columns can still drop rows, and ExpansionWatch clears expansion when it does
      const samePage = reconciled.query === state.query && state.page === 1
      return samePage ? { ...moved, expanded: state.expanded } : moved
    }
    case ACTIONS.selectionSet:
      return {
        ...state,
        selection: { ...state.selection, ...action.selection },
      }
    case ACTIONS.controlledMerge: {
      const incoming = action.state || {}
      const merged = {
        ...state,
        ...incoming,
        query: { ...state.query, ...(incoming.query || {}) },
        view: { ...state.view, ...(incoming.view || {}) },
      }
      if (sameQuery(state.query, merged.query)) {
        // an unchanged query keeps its identity, so a view-only merge does not read as a new request
        const kept = { ...merged, query: state.query }
        if (kept.page === state.page && kept.pageSize === state.pageSize)
          return kept
        // a supplied page or page size follows the pager's rules: query-scoped selection survives
        const page =
          kept.pageSize !== state.pageSize && !Number.isInteger(incoming.page)
            ? 1
            : kept.page
        return {
          ...kept,
          page,
          expanded: new Set(),
          selection: withoutPageSelection(state),
        }
      }
      // a supplied query is still a query change: selection and expansion belonged to the old one
      const moved = firstPage(merged)
      return Number.isInteger(incoming.page)
        ? { ...moved, page: incoming.page }
        : moved
    }
    default:
      return state
  }
}

/**
 * @param {import('../types').TableState} state
 * @param {import('../types').Action & object} action
 * @returns {import('../types').TableState}
 */
export const reducer = (state, action) => {
  const next = transition(state, action)
  if (next === state) return state
  return { ...next, reason: action.reason || null }
}
